import { WritableSignal, signal } from '@angular/core';
import { ErrorStateMatcher } from '@angular/material/core';
import { IInputMask, IMaskOptions, INumericInputMask, findLastNotation } from '@partners/utils';
import { ExtendedFormControl } from '../controls/control';
import { composeAsyncValidators, composeValidators } from '../functions';
import { IFieldAsserts, IFieldValidation, IFormField } from './form.interface';
import {
  TControlChangeTrigger,
  TFieldBeforeValueChange,
  TFieldDestroy,
  TFieldHint,
  TFieldInitialize,
  TFieldInputMethod,
  TFieldInputMode,
  TFieldReset,
  TFieldSelect,
  TFieldSelector,
  TFieldStateArgs,
  TFieldType,
  TFieldValueChange,
  TFormElement,
  TIterableOption
} from './form.types';

/**
 * @description
 * a helper base class to all form field types.
 *
 * the class encapsulates all the field's config (hint | errors | placeholder | mask | events.. etc')
 * including life cycle hooks method implementation and validators.
 *
 * it also creates an instance of a local FormControl to accept the validation and other config data
 * this control will be binded to the ng form
 */
export class FormField<T = unknown> implements IFormField<T> {
  name?: string;
  path?: string;
  index?: number;
  value?: T = null;
  mask?: IMaskOptions;
  masking?: IInputMask | INumericInputMask;
  label?: string;
  minorLabel?: string;
  inputType?: TFieldType;
  inputMode?: TFieldInputMode;
  inputMethod?: TFieldInputMethod;
  hint?: TFieldHint;
  placeholder?: string;
  elementType?: TFormElement;
  extraData: { [key: string]: any };
  validation?: IFieldValidation;
  inputSelector?: TFieldSelector;
  transformation?: IFieldAsserts;
  debounceInterval?: number;
  throttleInterval?: number;
  disabled?: boolean;
  hidden?: boolean;
  changeTrigger: TControlChangeTrigger;
  bidi?: boolean;
  isPending?: boolean;
  control?: ExtendedFormControl<T>;
  updateOn?: 'change';
  errorMatcher?: ErrorStateMatcher;
  valueChange?: TFieldValueChange<T>;
  beforeChange?: TFieldBeforeValueChange<T>;
  onDestroy?: TFieldDestroy;
  onInit?: TFieldInitialize<T>;
  onReset?: TFieldReset<T>;
  onSelect?: TFieldSelect<T>;
  keyProp?: string;
  valueProp?: string;
  options$$?: TIterableOption[];
  maxOptions?: number;
  detectChangeAt?: number;

  update: (args: TFieldStateArgs<T>) => void;
  state$$: WritableSignal<TFieldStateArgs<T>>;

  constructor({
    path,
    label,
    minorLabel,
    mask,
    masking,
    hint,
    value,
    index,
    validation,
    elementType,
    inputType,
    inputMode,
    updateOn,
    bidi,
    inputMethod,
    debounceInterval,
    throttleInterval,
    keyProp,
    valueProp,
    errorMatcher,
    disabled,
    hidden,
    extraData,
    changeTrigger,
    maxOptions,
    detectChangeAt,
    isPending,
    valueChange,
    beforeChange,
    onInit,
    onReset,
    onSelect,
    onDestroy
  }: IFormField<T>) {
    this.state$$ = signal<TFieldStateArgs<T>>(null);

    /**
     * name is a computed value. its source is the path property, its value is the last annotation of the
     * fields's complete path (example: if path is "residence.zipCode" then name is "zipCode". if path is
     * "firstName" then name is "firstName"
     **/
    this.name = findLastNotation(path);

    /** the string dot annotated path representing the hierarchial placement of the field ("residence.zipCode") */
    this.path = path;

    /** the label (placeholder) text of the form control */
    this.label = label;

    /** a sub label text displayed right next to the label (in smaller font) */
    this.minorLabel = minorLabel;

    /** a hint text displayed below the control */
    this.hint = hint;

    /** the initialized control's value */
    this.value = value;
    this.index = index;

    /** a mask pattern config object (IMaskOptions) */
    this.mask = mask;
    this.masking = masking;

    /** a boolean indicator of an RTL input */
    this.bidi = bidi;

    /** any data a custom control might need */
    this.extraData = extraData;

    /** a validation object holds collections of sync | async validators config objects */
    this.validation = validation;

    /** a custom error matcher */
    this.errorMatcher = errorMatcher;

    /** ng abstract form control type 'control' | 'group' | 'array' */
    this.elementType = elementType ?? 'control';

    /** a DOM input type ('text' | 'tel' | 'email' | 'date' | 'number') */
    this.inputType = inputType ?? 'text';
    this.inputMode = inputMode ?? 'text';

    /** how the input gets its value ('typing' | 'selecting' | 'checking') */
    this.inputMethod = inputMethod ?? 'typing';

    this.changeTrigger = changeTrigger ?? 'always';

    /** debounce interval time to be used when debouncing control valueChange callbacks */
    this.debounceInterval = debounceInterval ?? 0;
    this.throttleInterval = throttleInterval ?? 0;

    /** form control's disabled state */
    this.disabled = disabled;

    /** form control's hidden state */
    this.hidden = hidden;

    this.maxOptions = maxOptions;
    this.isPending = isPending;
    this.detectChangeAt = detectChangeAt;

    /** form control value change callback */
    this.valueChange = valueChange;
    this.beforeChange = beforeChange;

    /** form control value init callback */
    this.onInit = onInit;
    this.onReset = onReset;

    this.onSelect = onSelect;

    /** destroy callback for cleanup */
    this.onDestroy = onDestroy;

    this.update = this.updateState;

    /**
     * for an iterable control (i.e: dropdown, tiles, table, radio, etc') - we need to init
     * the required props to handle a selection functionality
     */
    if (this.inputMethod === 'selecting') {
      this.keyProp = keyProp ?? 'key';
      this.valueProp = valueProp ?? 'value';
    }

    /** create the actual ng form control & set its validators */

    this.control = new ExtendedFormControl<T>({ disabled: this.disabled, value }, { updateOn });

    this.control.setValidators(composeValidators(this.validation?.validationHandlers));
    this.control.setAsyncValidators(composeAsyncValidators(this.validation?.validationHandlers));

    this.setValidationLabel(validation);
  }

  /**
   * @description
   * each field has a label which functions as a floating placeholder. in the validation messages we often
   * need to use the value of the placeholder, for example: if { field: { label: 'שם פרטי' } } is required -
   * then the required error message will read: 'נראה שחסר כאן ' + field.label, this works fine in 95% cases
   * however sometimes a custom label is needed spcifically for validation message, for example:
   * { field: { label: 'באיזה יום בחודש יהיה לך נוח לשלם את ההחזר החודשי?' } } - in this case we cant
   * error message: 'נראה שחסר כאן ' + 'באיזה יום בחודש יהיה לך נוח לשלם את ההחזר החודשי' - so we would
   * declare a label on the *validation object* (field.validation: { label: '', validators[] }) to override
   * the default (field) label
   *
   * the mapping below copies the field label onto the validation label, in case no validation label was provided.
   * @param validation the field's validation object. it holds the validators collection
   **/
  private setValidationLabel(validation: IFieldValidation): void {
    validation?.validationHandlers.map((v) => {
      v.label = validation?.label ?? this.label;
    });
  }

  /**
   * @description
   * inject updated values to the field's properties. this is helpful when we want to
   * change the options[] collection of a dropdown or toggle a control's disabled state
   * or any other update that is required to reflect in the field
   * @param a destructed object that contains props of the field which can be updated
   * (type of destructed object is: TFieldStateArgs<T>)
   */
  private updateState = (args: Partial<TFieldStateArgs<T>>) => {
    let state: Partial<TFieldStateArgs<T>> = {};

    state = this.stateMember<T>('value', args, state);
    state = this.stateMember<boolean>('disabled', args, state);
    state = this.stateMember<boolean>('hidden', args, state);
    state = this.stateMember<boolean>('required', args, state);
    state = this.stateMember<string>('hint', args, state);
    state = this.stateMember<string>('label', args, state);
    state = this.stateMember<string>('minorLabel', args, state);
    state = this.stateMember<string>('mask', args, state);
    state = this.stateMember<unknown>('extraData', args, state);
    state = this.stateMember<boolean>('options', args, state);
    state = this.stateMember<boolean>('isPending', args, state);
    state = this.stateMember<IFieldValidation[] | boolean>('validation', args, state);

    this.state$$.set({ ...state });
  };

  private stateMember<S>(
    member: keyof TFieldStateArgs<T>,
    args: Partial<TFieldStateArgs<T>>,
    state: Partial<TFieldStateArgs<T>>
  ): Partial<TFieldStateArgs<T>> {
    const isMemberInArgs = member in args;

    if (!isMemberInArgs) {
      return state;
    }

    const memberValue = args[member] as S;
    return { ...state, ...{ [member]: memberValue } };
  }
}
